/**
 * Storage Integrity Service - Angular 16 Implementation
 * Provides secure localStorage management with integrity validation
 * Compatible with Angular 16+ and TypeScript 5+
 *
 * @version 1.1.0
 */

import { Injectable } from '@angular/core';
import {
    StorageIntegrityConfig,
    SecureStorageData,
    IntegrityValidationResult,
    IntegrityViolationReason,
    SecurityEvent,
    SecurityEventType,
    StorageOptions,
    RecoveryOptions,
    StorageHealth,
    SecurityEventCallback,
    StorageLogger,
} from './storage-integrity.interfaces';
import {
    DEFAULT_STORAGE_CONFIG,
    STORAGE_FORMAT_VERSION,
    validateConfig,
} from './storage-integrity.config';

@Injectable({
    providedIn: 'root',
})
export class StorageIntegrityService {
    private config: StorageIntegrityConfig;
    private logger: StorageLogger;

    private securityEventCallbacks: SecurityEventCallback[] = [];
    private integrityViolationCallbacks: SecurityEventCallback[] = [];
    private dataCorruptionCallbacks: SecurityEventCallback[] = [];

    constructor() {
        this.config = { ...DEFAULT_STORAGE_CONFIG };
        this.logger = this.createDefaultLogger();
        this.initializeService();
    }

    public initialize(config: Partial<StorageIntegrityConfig>): void {
        const newConfig = { ...this.config, ...config };
        const validation = validateConfig(newConfig);

        if (!validation.valid) {
            const error = `StorageIntegrityService configuration invalid: ${validation.errors.join(
                ', ',
            )}`;
            this.logger.error(error);
            throw new Error(error);
        }

        this.config = newConfig;
    }

    public setLogger(logger: StorageLogger): void {
        this.logger = logger;
    }

    public async setSecureData<T>(
        key: string,
        data: T,
        options: StorageOptions = {},
    ): Promise<boolean> {
        try {
            const fullKey = this.getFullKey(key);
            const timestamp = Date.now();

            const isProduction = !this.config.debug;
            const secureData: SecureStorageData = {
                data,
                signature: '',
                timestamp,
                version: STORAGE_FORMAT_VERSION,
                metadata: isProduction
                    ? { source: 'SI', ...options.metadata }
                    : { source: 'StorageIntegrityService', ...options.metadata },
            };

            secureData.signature = await this.generateSignature(secureData);

            localStorage.setItem(fullKey, JSON.stringify(secureData));

            this.emitSecurityEvent({
                type: SecurityEventType.DATA_STORED,
                timestamp,
                key: fullKey,
                payload: {
                    dataSize: JSON.stringify(data).length,
                    hasMetadata: !!options.metadata,
                },
            });

            return true;
        } catch (error) {
            this.logger.error(`Failed to store secure data for key ${key}:`, error);
            return false;
        }
    }

    public async getSecureData<T = unknown>(
        key: string,
        options: StorageOptions = {},
    ): Promise<T | null> {
        try {
            const fullKey = this.getFullKey(key);
            const rawData = localStorage.getItem(fullKey);

            if (!rawData) {
                return null;
            }

            let secureData: SecureStorageData;
            try {
                secureData = JSON.parse(rawData);
            } catch (parseError) {
                this.handleCorruption(key, 'JSON parse failed', {
                    skipValidation: options.skipValidation,
                });
                return null;
            }

            if (options.skipValidation) {
                this.logger.warn(`Skipping validation for key: ${key}`);
                return secureData.data as T;
            }

            const validation = await this.validateIntegrity(key, secureData);

            if (!validation.isValid) {
                this.handleIntegrityViolation(key, validation, secureData);
                return null;
            }

            if (this.isExpired(secureData)) {
                this.handleExpiration(key, secureData);
                return null;
            }

            this.emitSecurityEvent({
                type: SecurityEventType.DATA_RETRIEVED,
                timestamp: Date.now(),
                key: fullKey,
                payload: {
                    dataAge: Date.now() - secureData.timestamp,
                    version: secureData.version,
                },
            });

            return secureData.data as T;
        } catch (error) {
            this.logger.error(`Failed to retrieve secure data for key ${key}:`, error);
            return null;
        }
    }

    public async validateIntegrity(
        key: string,
        data?: SecureStorageData,
    ): Promise<IntegrityValidationResult> {
        try {
            const timestamp = Date.now();

            if (!data) {
                const fullKey = this.getFullKey(key);
                const rawData = localStorage.getItem(fullKey);

                if (!rawData) {
                    return {
                        isValid: false,
                        reason: IntegrityViolationReason.MISSING_SIGNATURE,
                        details: 'No data found',
                        validatedAt: timestamp,
                    };
                }

                try {
                    data = JSON.parse(rawData);
                } catch (parseError) {
                    return {
                        isValid: false,
                        reason: IntegrityViolationReason.INVALID_FORMAT,
                        details: 'JSON parse failed',
                        validatedAt: timestamp,
                    };
                }
            }

            if (!data) {
                return {
                    isValid: false,
                    reason: IntegrityViolationReason.INVALID_FORMAT,
                    details: 'Data is undefined after parsing',
                    validatedAt: timestamp,
                };
            }

            if (!data.signature) {
                return {
                    isValid: false,
                    reason: IntegrityViolationReason.MISSING_SIGNATURE,
                    details: 'No signature found in data',
                    validatedAt: timestamp,
                };
            }

            const expectedSignature = await this.generateSignature(data);

            if (data.signature !== expectedSignature) {
                return {
                    isValid: false,
                    reason: IntegrityViolationReason.SIGNATURE_MISMATCH,
                    details: 'Signature mismatch detected',
                    validatedAt: timestamp,
                };
            }

            if (data.version !== STORAGE_FORMAT_VERSION) {
                this.logger.warn(
                    `Version mismatch for key ${key}: ${data.version} vs ${STORAGE_FORMAT_VERSION}`,
                );
            }

            return {
                isValid: true,
                validatedAt: timestamp,
            };
        } catch (error) {
            this.logger.error(`Validation failed for key ${key}:`, error);
            return {
                isValid: false,
                reason: IntegrityViolationReason.DATA_CORRUPTED,
                details: `Validation error: ${(error as Error).message}`,
                validatedAt: Date.now(),
            };
        }
    }

    public clearSecureData(key?: string): void {
        try {
            if (key) {
                const fullKey = this.getFullKey(key);
                localStorage.removeItem(fullKey);
            } else {
                const keysToRemove: string[] = [];
                for (let i = 0; i < localStorage.length; i++) {
                    const storageKey = localStorage.key(i);
                    if (
                        storageKey &&
                        storageKey.startsWith(this.config.keyPrefix || '')
                    ) {
                        keysToRemove.push(storageKey);
                    }
                }

                keysToRemove.forEach((k) => localStorage.removeItem(k));
                this.logger.info(
                    `Cleared all secure data (${keysToRemove.length} keys)`,
                );
            }
        } catch (error) {
            this.logger.error('Failed to clear secure data:', error);
        }
    }

    public async detectManipulation(): Promise<boolean> {
        try {
            const keys = this.getAllSecureKeys();
            let manipulationDetected = false;

            for (const key of keys) {
                const validation = await this.validateIntegrity(key);
                if (
                    !validation.isValid &&
                    validation.reason === IntegrityViolationReason.SIGNATURE_MISMATCH
                ) {
                    manipulationDetected = true;
                    this.logger.warn(`Manipulation detected for key: ${key}`);
                }
            }

            return manipulationDetected;
        } catch (error) {
            this.logger.error('Failed to detect manipulation:', error);
            return false;
        }
    }

    public async recoverFromCorruption(
        options: RecoveryOptions = {},
    ): Promise<boolean> {
        try {
            const {
                attemptRecovery = this.config.autoRecovery,
                clearOnFailure = true,
                logoutOnFailure = true,
            } = options;

            this.logger.info('Attempting recovery from corruption...');

            if (!attemptRecovery) {
                this.logger.info(
                    'Auto-recovery disabled, clearing corrupted data',
                );
                this.clearSecureData();
                return false;
            }

            const keys = this.getAllSecureKeys();
            let recoveredCount = 0;
            let failedCount = 0;

            for (const key of keys) {
                const validation = await this.validateIntegrity(key);
                if (!validation.isValid) {
                    if (clearOnFailure) {
                        this.clearSecureData(this.getShortKey(key));
                        failedCount++;
                    }
                } else {
                    recoveredCount++;
                }
            }

            const success = failedCount === 0;

            this.emitSecurityEvent({
                type: SecurityEventType.DATA_RECOVERED,
                timestamp: Date.now(),
                key: 'all',
                payload: {
                    recoveredCount,
                    failedCount,
                    success,
                },
            });

            if (!success && logoutOnFailure) {
                this.triggerSecurityLogout();
            }

            this.logger.info(
                `Recovery completed: ${recoveredCount} recovered, ${failedCount} failed`,
            );
            return success;
        } catch (error) {
            this.logger.error('Recovery failed:', error);
            return false;
        }
    }

    public triggerSecurityLogout(): void {
        try {
            this.logger.warn(
                'Triggering security logout due to integrity violation',
            );

            this.emitSecurityEvent({
                type: SecurityEventType.AUTOMATIC_LOGOUT,
                timestamp: Date.now(),
                key: 'security',
                payload: {
                    reason: IntegrityViolationReason.SIGNATURE_MISMATCH,
                    automatic: true,
                },
            });

            this.clearSecureData();

            this.emitIntegrityViolation({
                type: SecurityEventType.AUTOMATIC_LOGOUT,
                timestamp: Date.now(),
                key: 'security',
            });
        } catch (error) {
            this.logger.error('Failed to trigger security logout:', error);
        }
    }

    public async validateUserData(
        userData: unknown,
        providedHash: string,
    ): Promise<boolean> {
        try {
            if (!userData || !providedHash) {
                return false;
            }

            const computedHash = await this.hmacSha256(
                JSON.stringify(userData),
                this.config.secretKey,
            );
            return computedHash === providedHash;
        } catch (error) {
            this.logger.error('Failed to validate user data:', error);
            return false;
        }
    }

    public async generateSecuritySignature(
        userId: number | string,
        email: string,
        token: string,
        options: { includeTimestamp?: boolean; timestamp?: number } = {},
    ): Promise<{ signature: string; issuedAt?: number }> {
        if (!userId || !email || !token) {
            throw new Error('Security signature requires userId, email and token');
        }

        const includeTimestamp = options.includeTimestamp !== false;
        const issuedAt = includeTimestamp
            ? options.timestamp ?? Math.floor(Date.now() / 1000)
            : undefined;

        const numericUserId = typeof userId === 'number' ? userId : Number(userId);
        const payload: Record<string, unknown> = {
            email,
            token,
            user_id: Number.isNaN(numericUserId) ? String(userId) : numericUserId,
        };

        if (issuedAt !== undefined) {
            payload.issued_at = issuedAt;
        }

        const sortedPayload: Record<string, unknown> = {};
        Object.keys(payload)
            .filter((key) => {
                const value = payload[key];
                return value !== undefined && value !== null && value !== '';
            })
            .sort()
            .forEach((key) => {
                sortedPayload[key] = payload[key];
            });

        const payloadString = JSON.stringify(sortedPayload);
        const signature = await this.hmacSha256(payloadString, this.config.secretKey);

        return issuedAt !== undefined ? { signature, issuedAt } : { signature };
    }

    public async getStorageHealth(): Promise<StorageHealth> {
        try {
            const keys = this.getAllSecureKeys();
            let validItems = 0;
            let corruptedItems = 0;
            let expiredItems = 0;
            let totalSize = 0;

            for (const key of keys) {
                try {
                    const rawData = localStorage.getItem(key);
                    if (rawData) {
                        totalSize += rawData.length;
                        const data: SecureStorageData = JSON.parse(rawData);

                        if (this.isExpired(data)) {
                            expiredItems++;
                        } else {
                            const validation = await this.validateIntegrity(
                                this.getShortKey(key),
                                data,
                            );
                            if (validation.isValid) {
                                validItems++;
                            } else {
                                corruptedItems++;
                            }
                        }
                    }
                } catch (error) {
                    corruptedItems++;
                }
            }

            return {
                totalItems: keys.length,
                validItems,
                corruptedItems,
                expiredItems,
                storageSize: totalSize,
                lastIntegrityCheck: Date.now(),
                storageAvailable: this.isStorageAvailable(),
            };
        } catch (error) {
            this.logger.error('Failed to get storage health:', error);
            return {
                totalItems: 0,
                validItems: 0,
                corruptedItems: 0,
                expiredItems: 0,
                storageSize: 0,
                lastIntegrityCheck: Date.now(),
                storageAvailable: false,
            };
        }
    }

    public onSecurityEvent(callback: SecurityEventCallback): () => void {
        this.securityEventCallbacks.push(callback);
        return () => {
            const index = this.securityEventCallbacks.indexOf(callback);
            if (index > -1) {
                this.securityEventCallbacks.splice(index, 1);
            }
        };
    }

    public onIntegrityViolation(callback: SecurityEventCallback): () => void {
        this.integrityViolationCallbacks.push(callback);
        return () => {
            const index = this.integrityViolationCallbacks.indexOf(callback);
            if (index > -1) {
                this.integrityViolationCallbacks.splice(index, 1);
            }
        };
    }

    public onDataCorruption(callback: SecurityEventCallback): () => void {
        this.dataCorruptionCallbacks.push(callback);
        return () => {
            const index = this.dataCorruptionCallbacks.indexOf(callback);
            if (index > -1) {
                this.dataCorruptionCallbacks.splice(index, 1);
            }
        };
    }

    private initializeService(): void {
        this.logger.info('StorageIntegrityService initialized');

        if (!this.isStorageAvailable()) {
            this.logger.error('localStorage is not available');
            throw new Error('localStorage is not available');
        }

        if (this.config.debug) {
            (async () => {
                try {
                    await this.getStorageHealth();
                } catch (error) {
                    this.logger.error('Failed to perform initial health check:', error);
                }
            })();
        }
    }

    private async generateSignature(data: SecureStorageData): Promise<string> {
        try {
            const dataForSigning = {
                data: data.data,
                timestamp: data.timestamp,
                version: data.version,
                metadata: data.metadata,
            };

            const dataString = JSON.stringify(dataForSigning);
            return await this.hmacSha256(dataString, this.config.secretKey);
        } catch (error) {
            this.logger.error('Failed to generate signature:', error);
            throw new Error('Signature generation failed');
        }
    }

    private async hmacSha256(data: string, key: string): Promise<string> {
        if (
            typeof window !== 'undefined' &&
            window.crypto &&
            window.crypto.subtle
        ) {
            try {
                return await this.hmacSubtleCrypto(data, key);
            } catch (error) {
                this.logger.warn('SubtleCrypto failed, falling back to legacy implementation:', error);
                return this.hmacFallback(data, key);
            }
        }

        return this.hmacFallback(data, key);
    }

    private async hmacSubtleCrypto(data: string, key: string): Promise<string> {
        const algorithm =
            this.config.algorithm === 'sha512' ? 'SHA-512' : 'SHA-256';
        const encoder = new TextEncoder();
        const keyData = encoder.encode(key);
        const dataToSign = encoder.encode(data);

        const cryptoKey = await window.crypto.subtle.importKey(
            'raw',
            keyData,
            { name: 'HMAC', hash: algorithm },
            false,
            ['sign'],
        );

        const signatureBuffer = await window.crypto.subtle.sign(
            'HMAC',
            cryptoKey,
            dataToSign,
        );

        return Array.from(new Uint8Array(signatureBuffer))
            .map((b) => b.toString(16).padStart(2, '0'))
            .join('');
    }

    private hmacFallback(data: string, key: string): string {
        if (this.config.strictMode) {
            const errorMsg = 'SubtleCrypto required in production mode but not available.';
            this.logger.error(errorMsg);
            throw new Error(errorMsg);
        }

        let hash = 0;
        const input = key + data + key;

        for (let i = 0; i < input.length; i++) {
            const char = input.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash;
        }

        const hexHash = Math.abs(hash).toString(16).padStart(8, '0');
        return (hexHash + hexHash + hexHash + hexHash).substring(0, 32);
    }

    private isExpired(data: SecureStorageData): boolean {
        if (!this.config.maxAge) {
            return false;
        }
        return Date.now() - data.timestamp > this.config.maxAge;
    }

    private isStorageAvailable(): boolean {
        try {
            const test = '__storage_test__';
            localStorage.setItem(test, test);
            localStorage.removeItem(test);
            return true;
        } catch (error) {
            return false;
        }
    }

    private getFullKey(key: string): string {
        return `${this.config.keyPrefix}${key}`;
    }

    private getShortKey(fullKey: string): string {
        return fullKey.replace(this.config.keyPrefix || '', '');
    }

    private getAllSecureKeys(): string[] {
        const keys: string[] = [];
        const prefix = this.config.keyPrefix || '';

        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(prefix)) {
                keys.push(key);
            }
        }

        return keys;
    }

    private handleIntegrityViolation(
        key: string,
        validation: IntegrityValidationResult,
        data: SecureStorageData,
    ): void {
        const event: SecurityEvent = {
            type: SecurityEventType.INTEGRITY_VIOLATION,
            timestamp: Date.now(),
            key,
            payload: {
                reason: validation.reason,
                details: validation.details,
                originalData: this.config.debug ? data : undefined,
            },
        };

        this.emitSecurityEvent(event);
        this.emitIntegrityViolation(event);

        if (
            this.config.strictMode &&
            validation.reason === IntegrityViolationReason.SIGNATURE_MISMATCH
        ) {
            this.triggerSecurityLogout();
        }
    }

    private handleCorruption(
        key: string,
        details: string,
        options: { skipValidation?: boolean } = {},
    ): void {
        const event: SecurityEvent = {
            type: SecurityEventType.CORRUPTION_DETECTED,
            timestamp: Date.now(),
            key,
            payload: {
                details,
                recoveryAttempted:
                    this.config.autoRecovery && !options.skipValidation,
            },
        };

        this.emitSecurityEvent(event);
        this.emitDataCorruption(event);

        if (this.config.autoRecovery && !options.skipValidation) {
            (async () => await this.recoverFromCorruption())();
        }
    }

    private handleExpiration(key: string, data: SecureStorageData): void {
        this.clearSecureData(key);

        this.emitSecurityEvent({
            type: SecurityEventType.DATA_RETRIEVED,
            timestamp: Date.now(),
            key,
            payload: {
                reason: IntegrityViolationReason.EXPIRED,
                dataAge: Date.now() - data.timestamp,
            },
        });
    }

    private emitSecurityEvent(event: SecurityEvent): void {
        this.securityEventCallbacks.forEach((callback) => {
            try {
                callback(event);
            } catch (error) {
                this.logger.error('Error in security event callback:', error);
            }
        });
    }

    private emitIntegrityViolation(event: SecurityEvent): void {
        this.integrityViolationCallbacks.forEach((callback) => {
            try {
                callback(event);
            } catch (error) {
                this.logger.error(
                    'Error in integrity violation callback:',
                    error,
                );
            }
        });
    }

    private emitDataCorruption(event: SecurityEvent): void {
        this.dataCorruptionCallbacks.forEach((callback) => {
            try {
                callback(event);
            } catch (error) {
                this.logger.error('Error in data corruption callback:', error);
            }
        });
    }

    private createDefaultLogger(): StorageLogger {
        return {
            debug: (message: string, ...args: unknown[]) => {
                if (this.config.debug) {
                    console.debug('[StorageIntegrity]', message, ...args);
                }
            },
            info: (message: string, ...args: unknown[]) => {
                if (this.config.debug) {
                    console.info('[StorageIntegrity]', message, ...args);
                }
            },
            warn: (message: string, ...args: unknown[]) => {
                if (
                    this.config.debug ||
                    (typeof window !== 'undefined' &&
                        window.location.hostname === 'localhost')
                ) {
                    console.warn('[StorageIntegrity]', message, ...args);
                }
            },
            error: (message: string, ...args: unknown[]) => {
                if (
                    this.config.debug ||
                    (typeof window !== 'undefined' &&
                        window.location.hostname === 'localhost')
                ) {
                    console.error('[StorageIntegrity]', message, ...args);
                }
            },
        };
    }
}
